import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  TableSortLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AddTaskIcon from "@mui/icons-material/AddTask";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import { getLoginUserData } from "../../../services/account.service";
import { ROLES } from "../../../constants/general.constant";
import AddCommunicationNote from "./AddCommunicationNote";
import {
  createLeadNote,
  deleteLeadNote,
  updateLeadNote,
} from "../../../services/lead.service";
import LeadNotesRow from "./LeadNotesRow";
import { format  } from "date-fns";
import { formatPhoneNumber } from "../../../helper/phoneFormatter.helper";

const getStatusColor = (status: string) => {
  switch (status) {
    case "Pending":
      return "warning";
    case "Public Lead":
      return "secondary";
    case "Invited":
      return "info";
    case "Registered":
      return "success";
    default:
      return "default";
  }
};

type Order = "asc" | "desc";

interface LeadTableViewProps {
  leads: any[];
  onEdit: (lead: any) => void;
  onDelete: (lead: any) => void;
  onInvite: (lead: any) => void;
  onConvert: (lead: any) => void;
  onAddTask: (lead: any) => void;
  onFetch: (sortBy: string, sortOrder: Order) => void;
  query: any;
}

const LeadTableView: React.FC<LeadTableViewProps> = ({
  leads,
  onEdit,
  onDelete,
  onInvite,
  onConvert,
  onAddTask,
  onFetch,
  query
}) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteType, setDeleteType] = useState<"lead" | "note" | null>(null);

  const [user, setUser] = useState<any>(null);
  // const [orderBy, setOrderBy] = useState<string>(query?.sortBy || "followUpDate");
  // const [order, setOrder] = useState<Order>(query?.sortOrder || "asc");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Note dialog state
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [activeLead, setActiveLead] = useState<any>(null);
  const [editingNote, setEditingNote] = useState<any>(null);
  const [localLeads, setLocalLeads] = useState<any[]>(leads);

  const orderBy = query.sortBy;
  const order: Order = query.sortOrder.toLowerCase() as Order;

  useEffect(() => {
    setLocalLeads(leads); // sync when parent gives new data
  }, [leads]);

  const handleAddNote = (lead: any) => {
    setActiveLead(lead);
    setEditingNote(null);
    setOpenNoteDialog(true);
  };

  const handleEditNote = (note: any, lead: any) => {
    setActiveLead(lead);
    setEditingNote(note);
    setOpenNoteDialog(true);
  };

  const handleSaveNote = async (note: any) => {
    try {
      if (editingNote) {
        await handleUpdateNote(note);
      } else {
        await handleCreateNote(note);
      }

      onFetch(orderBy, order);
    } catch (error) {
      console.error("save lead note error", error);
    }
  };

  async function handleUpdateNote(note: any) {
    if (!editingNote || !activeLead) return;

    const updatedNote = await updateLeadNote(editingNote.id, note);

    setLocalLeads((prev) =>
      prev.map((lead) =>
        lead.id === activeLead.id ? updateLeadWithNote(lead, updatedNote) : lead
      )
    );
  }

  async function handleCreateNote(note: any) {
    if (!activeLead) return;

    const createdNote = await createLeadNote(activeLead.id, note);

    setLocalLeads((prev) =>
      prev.map((lead) =>
        lead.id === activeLead.id ? addNoteToLead(lead, createdNote) : lead
      )
    );
  }

  // --- Pure helper functions (no state access) ---
  function updateLeadWithNote(lead: any, updatedNote: any) {
    return {
      ...lead,
      notes: lead.notes.map((n: any) =>
        n.id === updatedNote.id ? { ...n, ...updatedNote } : n
      ),
    };
  }

  function addNoteToLead(lead: any, createdNote: any) {
    return {
      ...lead,
      notes: [createdNote, ...lead.notes],
    };
  }

  const handleDeleteLeadClick = (lead: any) => {
    setDeleteTarget(lead);
    setDeleteType("lead");
    setOpenDeleteDialog(true);
  };

  const handleDeleteNoteClick = (note: any) => {
    setDeleteTarget(note);
    setDeleteType("note");
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    if (deleteType === "lead") {
      handleDeleteLead(deleteTarget);
    } else if (deleteType === "note") {
      await handleDeleteNote(deleteTarget);
    }

    resetDeleteState();
  };

  const handleDeleteLead = (target: any) => {
    onDelete(target);
  };

  const handleDeleteNote = async (target: any) => {
    await deleteLeadNote(target.id);
    setLocalLeads((prev) => prev.map((lead) => updateLeadNotes(lead, target.id)));
  };

  const updateLeadNotes = (lead: any, noteId: string) => {
    const hasNote = lead.notes.some((n: any) => n.id === noteId);
    if (!hasNote) return lead;

    return {
      ...lead,
      notes: lead.notes.filter((n: any) => n.id !== noteId),
    };
  };

  const resetDeleteState = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget(null);
    setDeleteType(null);
  };

  const handleSort = (column: string) => {
    let newOrder: Order = "asc";

    if (orderBy === column) {
      newOrder = order === "asc" ? "desc" : "asc";
    }

    onFetch(column, newOrder);
  };

  const toggleExpand = (leadId: string) => {
    setExpandedRow((prev) => (prev === leadId ? null : leadId));
  };

  useEffect(() => {
    const init = async () => {
      const loggedInUser = await getLoginUserData();
      setUser(loggedInUser);
    };
    init();
  }, []);

  const columns: Array<{ id: string; label: string; sortable?: boolean }> = [
    { id: "expand", label: "", sortable: false },
    { id: "firstName", label: "Name", sortable: true },
    { id: "businessName", label: "Business Name", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "phone", label: "Phone", sortable: true },
    { id: "followUpDate", label: "Follow Up", sortable: true },
    { id: "comments", label: "Comments", sortable: false },
    ...(user?.role !== ROLES.EMPLOYEE
      ? [{ id: "createdBy", label: "Created By", sortable: false }]
      : []),
    { id: "status", label: "Status", sortable: true },
    { id: "actions", label: "Actions", sortable: false },
  ];

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f9fafb" }}>
              {columns.map((col) => {
                const isActive = orderBy === col.id;
                return (
                  <TableCell
                    key={col.id}
                    sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}
                    // sortDirection={isActive ? order : false}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={isActive}
                        direction={isActive ? order : "asc"}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {localLeads.map((lead) => {
              const lastSummary = lead?.notes?.[0]?.summary ?? null;
              const createdByName =
                `${(lead.createdByUser as any)?.firstName || ''} ${(lead.createdByUser as any)?.middleName || ''} ${(lead.createdByUser as any)?.lastName || ''}`.trim() ||
                `${(lead.createdByBusinessUser as any)?.name || ''}`.trim() || "-";
              return (
              <React.Fragment key={lead.id}>
                <TableRow hover>
                  {/* Expand Toggle */}
                  <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                    <IconButton size="small" onClick={() => toggleExpand(lead.id)}>
                      {expandedRow === lead.id ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </IconButton>
                  </TableCell>

                  <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                    {[lead.firstName, lead.middleName, lead.lastName].filter(Boolean).join(" ")}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                    {lead.businessName}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                    {lead.email || "-"}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                    {lead.phone ? formatPhoneNumber(lead.phone) : "-"}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                      {lead.nextFollowUpDate ? format(new Date(lead.nextFollowUpDate), "MM-dd-yyyy") : "-"}

                  </TableCell>
                  <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                    {lastSummary || lead.comments || "-"}
                  </TableCell>

                  {user?.role !== ROLES.EMPLOYEE && (
                    <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                      {createdByName}
                    </TableCell>
                  )}

                  <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                    <Chip
                      label={lead.status || "-"}
                      color={getStatusColor(lead.status)}
                      variant="filled"
                      size="small"
                    />
                  </TableCell>

                  <TableCell sx={{ border: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>
                    <Tooltip title="Convert to Customer">
                      <IconButton onClick={() => onConvert(lead)} size="small" color="success">
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add Task">
                      <IconButton onClick={() => onAddTask(lead)} size="small" color="info">
                        <AddTaskIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => onEdit(lead)} size="small" color="secondary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Invite">
                      <IconButton onClick={() => onInvite(lead)} size="small" color="primary">
                        <MailOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => handleDeleteLeadClick(lead)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* Expandable Notes Section */}
                <LeadNotesRow
                  lead={lead}
                  expandedRow={expandedRow}
                  handleAddNote={handleAddNote}
                  handleEditNote={(note) => handleEditNote(note, lead)}
                  handleDeleteNote={handleDeleteNoteClick}
                  colSpan={columns.length}
                />
              </React.Fragment>
            )
          })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Shared Confirm Dialog for Lead + Note */}
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        itemName={
          deleteType === "lead"
            ? [deleteTarget?.firstName, deleteTarget?.middleName, deleteTarget?.lastName].filter(Boolean).join(" ")
            : deleteType === "note"
            ? deleteTarget?.summary
            : ""
        }
      />

      {/* Add/Edit Note Dialog */}
      <AddCommunicationNote
        open={openNoteDialog}
        onClose={() => {
          setOpenNoteDialog(false);
          setEditingNote(null);
        }}
        onSave={handleSaveNote}
        note={editingNote}
      />
    </>
  );
};

export default LeadTableView;
